import CSREntry from 'src/clientRoutes/_app.market.csr'
import FullSSREntry from 'src/clientRoutes/_app.market.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { type ListGenerationModelsQueryVariables } from 'src/services/apollo/generated/graphql'
import {
  SSR_DUAL_MODE_EXPERIMENT,
  SSR_DUAL_MODE_EXPERIMENT_NAME,
} from 'src/utils/router/ssrShell'
import { isFullSsrTreatment } from 'src/utils/router/ssrShell.server'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.market'

export const handle = {
  name: 'market',
  track: { scene: 'models' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/**
 * SSR pre-fetch variables. **Must match what `BaseModelListInfiniteScroll`
 * generates on first mount with the default URL state**, otherwise the
 * cache key won't match and the live page will refetch (visible flash):
 *
 *   - default `order = 'trending'` (set in `useModelFilterState.defaults`)
 *   - default `keyword` undefined → `feed = 'trending'` (via
 *     `getPosSeqAndFeedForModelListQuery({order:'trending'})`)
 *   - `posSeq = false` for trending → uses `last: 24` (NOT `first: 12`)
 *   - all other variables (tag/tackId/keyword/authorId/types/timeRange/
 *     orderBy) resolve to `undefined`, which Apollo's cache key drops
 *
 * Keeping these in lockstep with `<BaseModelListInfiniteScroll>`
 * (`packages/pixai-app/src/components/GenerationModel/GenerationModelList/index.tsx`)
 * is the whole point of pre-fetching here. If the client query shape
 * changes, mirror the change here.
 */
const SSR_LIST_VARIABLES: ListGenerationModelsQueryVariables = {
  feed: 'trending',
  last: 24,
}

/** SSR loader. */
export async function loader({ request, context }: Route.LoaderArgs) {
  const { fetchListGenerationModelsForSSR } =
    await import('src/utils/ssr-meta/graphql/tag.server')
  const { t } = getI18n(context)
  const meta = staticMetaLoader({
    requestUrl: request.url,
    title: t('server:meta.market.title'),
    description: t('server:meta.market.description'),
  })

  // Phase 4 optimisation: skip the expensive `ListGenerationModels`
  // pre-fetch when this request is going to render the control
  // (CSR) entry. The control entry under `<RootRuntime>`'s global
  // Apollo client never reads `listConnection`, so the fetch would
  // be wasted server CPU + an extra GraphQL round-trip. Treatment
  // requests still pay the prefetch and seed `listConnection`
  // for the Full SSR Apollo bridge in
  // `clientRoutes/_app.market.full-ssr.tsx`.
  const listConnection = isFullSsrTreatment(
    context,
    SSR_DUAL_MODE_EXPERIMENT_NAME,
  )
    ? await fetchListGenerationModelsForSSR(SSR_LIST_VARIABLES).catch(
        () => null,
      )
    : null

  return {
    ...meta,
    listConnection,
  }
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

/**
 * Opt out of a `HydrateFallback`. We render the real page tree both on
 * the server and the client (no hand-written shell), so React Router
 * never has to interpose a fallback between SSR HTML and hydrated
 * output — the SSR DOM and the first hydrate commit are the same tree.
 */
export function HydrateFallback() {
  return null
}

/**
 * Dual-mode entry. `'off'` (CSR) path renders `MarketPage` directly
 * under `<RootRuntime>`'s global Apollo / QueryClient with no SSR
 * seed; `'on'` (Full SSR) path mounts the route-scoped Apollo
 * bridge (`SSR_LIST_VARIABLES` seeded into `ListGenerationModels`)
 * defined in `clientRoutes/_app.market.full-ssr.tsx`.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
